/**
 * 导航可见性注册表 + 角色默认首页。
 *
 * 背景：登录首页（landing）是按账号存在 localStorage / 服务端 prefs 里的。切换账号后，
 * 若沿用上个账号存的路径（如 admin 存的 /admin/ai），新账号会落到一个自己没权限的页面。
 * Layout 渲染时把「当前账号可见的导航路径」发布到这里，Landing 用它做校验。
 *
 * 注意：这里只用于「登录落地页」的校验，不做全局路由拦截——全局拦截会误伤
 * 不在侧栏导航里的页面（如 /reminders 只有部分角色有导航项，但铃铛对所有人可达）。
 */

let _allowed = null; // Set<string> | null（null = 尚未发布）

/** Layout 渲染时发布：当前账号「可见」的导航路径集合 */
export function setAllowedNavPaths(paths) {
  _allowed = new Set((paths || []).map((p) => String(p).split("?")[0]).filter(Boolean));
}

export function getAllowedNavPaths() {
  return _allowed;
}

/**
 * 路径是否对当前账号开放。
 * - 未发布（null）时返回 trustUnknown（默认 true，保持既有行为不误拦）
 * - 命中集合本身，或命中集合中某项的父路径（如 /crm/leads 覆盖 /crm/leads/123）都算开放
 */
export function isAllowedNavPath(path, { trustUnknown = true } = {}) {
  const p = String(path || "").split("?")[0].split("#")[0];
  if (!p || p === "/") return true;
  if (!_allowed) return trustUnknown;
  if (_allowed.has(p)) return true;
  for (const a of _allowed) {
    if (a !== "/" && p.startsWith(a + "/")) return true;
  }
  return false;
}

/**
 * 代操作的路由回退必须等当前账号的权限快照完成加载。
 * 深链首帧若用 member 等默认角色权限替代服务端快照，会把已获单独授权的账号误送到默认首页。
 */
export function shouldRedirectImpersonatedPath(user, permissionsLoading, pathVisible) {
  return !!user?.impersonatedBy && !permissionsLoading && !pathVisible;
}

/**
 * 角色默认登录页。返回 null 表示停在 "/"（公司总览 Dashboard）。
 * 与 App.js 里 Landing 的历史 if 链保持一致的顺序与映射。
 */
export function defaultLanding(user) {
  const r = user?.role;
  if (r === "member" || r === "aftersales" || r === "branch_sales_manager") return "/board";
  if (r === "sales_assistant") return "/hrms/me";
  if (r === "ceo") return "/ceo";
  if (r === "sales") return "/crm/leads";
  if (["sales_manager", "sales_vp", "marketing", "marketing_manager"].includes(r)) return "/crm/dashboard";
  if (["tm_manager", "telesales", "b2b_sales"].includes(r)) return "/tm/workbench";
  if (r === "data_importer") return "/tm/homeowners";
  /* 【2026-10-23 修复】eng_manager 原来指向 /rectify（整改看板），
     但该路由已在走查清单「二-7」随页面一起删除（App.js 里只剩一行注释）。
     结果是工程经理登录、或访问任何自己无权限的页面（Layout 会用 defaultLanding 弹回默认首页）时，
     被送去一个不存在的路由 → 整页空白，连左侧导航都没有，看起来就是「整个系统 error」。
     改指向 /board（工程项目），这是工程经理的主战场，与侧栏第一项一致。 */
  if (r === "eng_manager") return "/board";
  if (r === "cashier") return "/payments";
  if (r === "pm") return "/change-orders";
  if (r === "subcontractor") return "/portal";
  if (["warehouse_manager", "material_purchaser", "warehouse_auditor"].includes(r)) return "/warehouses";
  return null;
}
